import React, { useState, useEffect, useRef, useCallback } from 'react';
import { z } from 'zod';
import { UserPlusIcon } from '@heroicons/react/24/solid';
import { Header, Footer, PasswordInput } from '@ui';
import { getSupabaseClient } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import EmailConfirmation from './EmailConfirmation';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const COUNTRY_CODES = [
  { code: '+54', label: 'Argentina (+54)' },
  { code: '+61', label: 'Australia (+61)' },
  { code: '+591', label: 'Bolivia (+591)' },
  { code: '+55', label: 'Brazil (+55)' },
  { code: '+56', label: 'Chile (+56)' },
  { code: '+57', label: 'Colombia (+57)' },
  { code: '+506', label: 'Costa Rica (+506)' },
  { code: '+53', label: 'Cuba (+53)' },
  { code: '+593', label: 'Ecuador (+593)' },
  { code: '+503', label: 'El Salvador (+503)' },
  { code: '+33', label: 'France (+33)' },
  { code: '+49', label: 'Germany (+49)' },
  { code: '+502', label: 'Guatemala (+502)' },
  { code: '+592', label: 'Guyana (+592)' },
  { code: '+509', label: 'Haiti (+509)' },
  { code: '+504', label: 'Honduras (+504)' },
  { code: '+91', label: 'India (+91)' },
  { code: '+353', label: 'Ireland (+353)' },
  { code: '+39', label: 'Italy (+39)' },
  { code: '+81', label: 'Japan (+81)' },
  { code: '+52', label: 'Mexico (+52)' },
  { code: '+31', label: 'Netherlands (+31)' },
  { code: '+64', label: 'New Zealand (+64)' },
  { code: '+505', label: 'Nicaragua (+505)' },
  { code: '+1', label: 'North America (+1)' },
  { code: '+507', label: 'Panama (+507)' },
  { code: '+595', label: 'Paraguay (+595)' },
  { code: '+51', label: 'Peru (+51)' },
  { code: '+7', label: 'Russia (+7)' },
  { code: '+65', label: 'Singapore (+65)' },
  { code: '+27', label: 'South Africa (+27)' },
  { code: '+82', label: 'South Korea (+82)' },
  { code: '+34', label: 'Spain (+34)' },
  { code: '+597', label: 'Suriname (+597)' },
  { code: '+46', label: 'Sweden (+46)' },
  { code: '+41', label: 'Switzerland (+41)' },
  { code: '+971', label: 'United Arab Emirates (+971)' },
  { code: '+44', label: 'United Kingdom (+44)' },
  { code: '+598', label: 'Uruguay (+598)' },
  { code: '+58', label: 'Venezuela (+58)' },
];

import { registerSchema as schema } from './registerSchema';

type FormState = z.infer<typeof schema>;

export default function Register() {
  const [form, setForm] = useState<FormState>({
    fullName: '',
    email: '',
    phone: '',
    countryCode: '+1',
    password: '',
    confirmPassword: '',
    agree: false, // ✅ type now allows false as the initial value
  });

  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const navigate = useNavigate();

  const onTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const onTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  // Load Turnstile script and render widget
  useEffect(() => {
    const siteKey = (window as any).__ENV__?.TURNSTILE_SITE_KEY;
    if (!siteKey) return;

    const renderWidget = () => {
      if (!turnstileRef.current || widgetIdRef.current !== null) return;
      if (!window.turnstile) return;

      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: siteKey,
        theme: 'light',
        callback: onTurnstileVerify,
        'expired-callback': onTurnstileExpire,
        'error-callback': onTurnstileExpire,
      });
    };

    // If script already loaded, render immediately
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Load the script
    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      script.defer = true;

      (window as any).onTurnstileLoad = renderWidget;
      document.head.appendChild(script);
    } else {
      // Script exists but turnstile not ready yet — poll briefly
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          renderWidget();
        }
      }, 100);
      return () => clearInterval(interval);
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onTurnstileVerify, onTurnstileExpire]);

  // Format phone digits as (XXX) XXX-XXXX
  const formatPhone = (digits: string): string => {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target;
    if (name === 'phone') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setForm((prev) => ({ ...prev, phone: formatPhone(digits) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
    if (fieldErrors[name]) {
      setFieldErrors((fe) => {
        const next = { ...fe };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      parsed.error.errors.forEach((err) => {
        const key = err.path[0] as string;
        if (!fe[key]) fe[key] = err.message;
      });
      setFieldErrors(fe);
      setError(Object.values(fe)[0] ?? 'Please fix the form errors');
      return;
    }

    try {
      setSubmitting(true);
      const supabase = getSupabaseClient();

      // Verify Turnstile token server-side (skip if no site key configured)
      const hasTurnstile = !!(window as any).__ENV__?.TURNSTILE_SITE_KEY;
      if (hasTurnstile && turnstileToken) {
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-turnstile', {
          body: { token: turnstileToken },
        });

        if (verifyError || !verifyData?.success) {
          setError('Verification failed. Please try again.');
          if (widgetIdRef.current !== null && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
          }
          setTurnstileToken(null);
          setSubmitting(false);
          return;
        }
      }

      const sanitizedPhone = form.phone.replace(/\D/g, '');
      const fullPhone = `${form.countryCode}${sanitizedPhone}`;

      // Get the callback URL from the current window location
      const callbackUrl = `${window.location.origin}/auth/callback`;

      const { data, error: signUpErr } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: { full_name: form.fullName, phone: fullPhone, role: 'guide' },
          emailRedirectTo: callbackUrl,
        },
      });
      if (signUpErr) {
        setError(signUpErr.message);
        return;
      }

      const user = data.user;
      if (user) {
        // Create profile first with all user data
        const { error: profileErr } = await supabase.from('profiles').insert({
          id: user.id,
          email: form.email,
          role: 'guide',
          full_name: form.fullName,
          phone: fullPhone,
        });
        if (profileErr) console.warn('Insert profiles failed:', profileErr.message);

        // Then create guide record linked to the profile
        const { error: insertGuideErr } = await supabase.from('guides').insert({
          user_id: user.id, // Use id instead of user_id to match the profile
          status: 'pending',
        });
        if (insertGuideErr) console.warn('Insert guides failed:', insertGuideErr.message);
      }

      setSuccess('Account created successfully! Please check your email for a confirmation link to activate your account.');
      setRegisteredEmail(form.email);
      setRegistrationComplete(true);
    } catch (err) {
      console.error('Network error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (name: keyof FormState) =>
    `w-full rounded-xl border px-4 py-3 text-sm placeholder:text-[#2C2731]/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#2C2731] focus:border-[#2C2731] ${fieldErrors[name] ? 'bg-[#E8DFBD] border-[#2C2731]' : 'bg-[#FFFCEF] border-[#2C2731]'
    }`;


  const Label: React.FC<{ htmlFor?: string; children: React.ReactNode }> = ({ htmlFor, children }) => (
    <label htmlFor={htmlFor} className="block mb-1 text-sm font-medium text-[#2C2731]">
      <span className="text-[#241F2A]" aria-hidden="true">*</span>
      <span className="ml-1">{children}</span>
    </label>
  );

  // Show email confirmation page after successful registration
  if (registrationComplete && registeredEmail) {
    return <EmailConfirmation email={registeredEmail} />;
  }

  return (
    <div className="min-h-screen bg-[#FFFCEF] text-[#2C2731] flex flex-col">
      <Header variant="register" />

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-5xl rounded-2xl p-8 shadow-sm bg-[#FFFCEF]">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-14 w-14 rounded-full bg-[#E8DFBD] border border-[#2C2731] flex items-center justify-center text-[#2C2731]">
                  <UserPlusIcon className="h-6 w-6 text-[#2C2731]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#214D3C]/80">Start here</p>
                  <h1 className="text-3xl font-recoleta text-[#2C2731]">Create Your Account</h1>
                </div>
              </div>

              {error && (
                <div className="bg-[#2C2731] border border-[#FFFCEF] rounded-xl p-4 mb-4 text-[#FFFCEF]">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-[#FFFCEF]" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="bg-[#E8DFBD] border border-[#2C2731] rounded-xl p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-[#2C2731]" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-[#2C2731]">Registration Successful!</h3>
                      <div className="mt-2 text-sm text-[#2C2731]">
                        <p>We've sent a confirmation email to <strong>{form.email}</strong>. Please check your inbox and click the confirmation link to activate your account.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <p className="text-[#2C2731]/80 text-sm">
                  Set up your guide account so we can learn about your practice and connect you with aligned seekers.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Full Name */}
            <div>
              <Label htmlFor="fullName">Full Name</Label>
              <input
                id="fullName"
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={inputCls('fullName')}
                required
              />
              {fieldErrors.fullName && <p className="text-xs text-[#2C2731] mt-1">{fieldErrors.fullName}</p>}
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">Email Address</Label>
              <input
                id="email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                className={inputCls('email')}
                required
              />
              {fieldErrors.email && <p className="text-xs text-[#2C2731] mt-1">{fieldErrors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <div className="flex gap-3">
                <select
                  id="countryCode"
                  name="countryCode"
                  value={form.countryCode}
                  onChange={handleChange}
                  className={`${inputCls('countryCode')} w-48 md:w-60`}
                >
                  {COUNTRY_CODES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className={inputCls('phone')}
                  required
                />
              </div>
              {fieldErrors.countryCode && <p className="text-xs text-[#2C2731] mt-1">{fieldErrors.countryCode}</p>}
              {fieldErrors.phone && <p className="text-xs text-[#2C2731] mt-1">{fieldErrors.phone}</p>}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Create a strong password"
                className={inputCls('password')}
                required
                minLength={8}
              />
              <p className="text-xs text-[#2C2731]/60 mt-1">
                Must be at least 8 characters with letters and numbers
              </p>
              {fieldErrors.password && <p className="text-xs text-[#2C2731] mt-1">{fieldErrors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className={inputCls('confirmPassword')}
                required
              />
              {fieldErrors.confirmPassword && (
                <p className="text-xs text-[#2C2731] mt-1">{fieldErrors.confirmPassword}</p>
              )}
            </div>

            {/* Agree to Terms */}
            <div className="flex items-center text-sm text-[#2C2731]">
              <input
                id="agree"
                type="checkbox"
                name="agree"
                checked={form.agree}
                onChange={handleChange}
                className="mr-3 h-4 w-4 rounded border-[#2C2731] text-[#2C2731] focus:ring-0 focus:ring-offset-0 bg-[#FFFCEF]"
                required
              />
              <label htmlFor="agree" className="cursor-pointer">
                <span className="text-[#241F2A]" aria-hidden="true">*</span>{' '}
                I agree to the{' '}
                <a href="/terms/full" className="underline" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacypolicy" className="underline" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </label>
            </div>
            {fieldErrors.agree && <p className="text-xs text-[#2C2731]">{fieldErrors.agree}</p>}

            {/* Turnstile */}
            <div ref={turnstileRef} className="flex justify-center" />

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || (!!(window as any).__ENV__?.TURNSTILE_SITE_KEY && !turnstileToken)}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-full border px-6 py-3 text-base font-semibold transition ${
                submitting || (!!(window as any).__ENV__?.TURNSTILE_SITE_KEY && !turnstileToken)
                  ? 'border-[#304031]/40 bg-[#304031]/40 text-[#FFFCEF]/80 cursor-not-allowed'
                  : 'border-[#304031] bg-[#304031] text-[#FFFCEF] hover:bg-[#1F1924]'
              }`}
            >
              {submitting ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#FFFCEF] border-t-transparent" />
                  Creating...
                </>
              ) : (
                'Create Account'
              )}
            </button>

            <p className="text-xs text-[#2C2731]/60 mt-4 text-center">
              By creating an account, you'll be able to connect with aligned seekers, manage your pipeline, and track the full journey of support.
            </p>
              </form>
            </div>
            <div className="rounded-2xl bg-[#214D3C] p-8">
              <img src="/icon.svg" alt="" className="w-6 h-6 mb-4 [filter:brightness(0)_invert(98%)_sepia(6%)_saturate(365%)_hue-rotate(338deg)_brightness(103%)]" />
              <p className="text-lg font-bold text-[#FFFCEF]">
                What Your Guide Account Enables
              </p>
              <p className="mt-3 text-sm text-[#FFFCEF]">By creating your account you'll be able to:</p>
              <ul className="mt-5 space-y-3 text-sm text-[#FFFCEF]">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FFFCEF] flex-shrink-0" aria-hidden />
                  Connect with aligned seekers who match your specialties
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FFFCEF] flex-shrink-0" aria-hidden />
                  Manage your client pipeline through a private, secure dashboard
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FFFCEF] flex-shrink-0" aria-hidden />
                  Track your impact through preparation, journey support, and integration
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FFFCEF] flex-shrink-0" aria-hidden />
                  Access resources, trainings, and peer support designed for facilitators
                </li>
              </ul>
              <p className="mt-5 text-sm text-[#FFFCEF]">
                Your information is private, secure, and used only to support your work within the collective.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
